import { useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2, Sparkles } from "lucide-react";

const STEPS = ["Reading brief", "Spatial partitioning", "Placing openings", "Composing Rhino geometry"];
const SUGGESTIONS = ["Make the kitchen bigger", "Make it two storeys", "Add a 2-car garage", "Add a home office"];

const visibleText = (t) => t.split("```")[0].trim();

const Bubble = ({ m, i }) => (
  <div data-testid={`chat-message-${i}`} className={`rise ${m.role === "user" ? "ml-8" : "mr-4"}`}>
    <div className="font-mono text-[10px] uppercase tracking-widest mb-1.5 text-[var(--muted)]">{m.role === "user" ? "You" : "RhinoDraft AI"}</div>
    <div className={`text-sm leading-relaxed px-4 py-3 border ${m.role === "user" ? "bg-[var(--card)] border-[var(--line)]" : "border-[var(--cyan)]/25 bg-[var(--cyan)]/[0.04]"}`}>{m.content}</div>
  </div>
);

const Progress = ({ text }) => {
  const [secs, setSecs] = useState(0);
  useEffect(() => { const t = setInterval(() => setSecs((s) => s + 1), 1000); return () => clearInterval(t); }, []);
  const inJson = text.includes("```");
  const step = inJson ? Math.min(1 + Math.floor(text.length / 1800), 3) : 0;
  return (
    <div className="mr-4" data-testid="chat-streaming">
      <div className="font-mono text-[10px] uppercase tracking-widest mb-1.5 text-[var(--cyan)] flex items-center gap-2"><Loader2 size={11} className="animate-spin" /> {text ? "Drafting" : "Thinking through the layout"} · {secs}s</div>
      {visibleText(text) && <div className="text-sm leading-relaxed px-4 py-3 border border-[var(--cyan)]/25 bg-[var(--cyan)]/[0.04] mb-3">{visibleText(text)}</div>}
      <ol className="space-y-1.5 font-mono text-[11px]">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex items-center gap-2 ${i < step ? "text-[var(--emerald)]" : i === step ? "text-[var(--cyan)]" : "text-[var(--muted)]/50"}`}>
            <span className="w-4">{i < step ? "✓" : i === step ? "›" : "·"}</span>{s}
          </li>
        ))}
      </ol>
    </div>
  );
};

export const ChatPanel = ({ messages, streaming, streamText, onSend, hasPlan, locked = false, estimate }) => {
  const [text, setText] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, streamText]);

  const submit = (v) => {
    const msg = (v ?? text).trim();
    if (!msg || streaming) return;
    onSend(msg);
    setText("");
  };

  return (
    <div className="flex flex-col h-full" data-testid="chat-panel">
      <div className="flex-1 overflow-y-auto scroll-thin p-5 space-y-5">
        {messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        {streaming && <Progress text={streamText} />}
        <div ref={end} />
      </div>
      <div className="p-4 border-t border-[var(--line)] space-y-3">
        {hasPlan && !streaming && (
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => submit(s)} data-testid={`suggestion-${s.toLowerCase().replace(/\W+/g, "-")}`}
                className="text-[11px] px-2.5 py-1 border border-[var(--line)] text-[var(--muted)] hover:border-[var(--cyan)] hover:text-[var(--cyan)] transition-colors">{s}</button>
            ))}
          </div>
        )}
        {estimate && text.trim() && !streaming && !locked && (() => { const h = estimate(text); return h && (
          <p className="text-[11px] font-mono" data-testid="revision-estimate">{h}</p>); })()}
        <div className="flex items-end gap-2 bg-[var(--card)] border border-[var(--line)] focus-within:border-[var(--cyan)] transition-colors p-2">
          <Sparkles size={16} className="text-[var(--cyan)] mb-2 ml-1 shrink-0" />
          <textarea data-testid="chat-input" rows={2} value={text} disabled={streaming || locked}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder={hasPlan ? "Ask for a revision…" : "Describe the building you need…"}
            className="flex-1 bg-transparent resize-none outline-none text-sm placeholder:text-[var(--muted)]/60 py-1" />
          <button data-testid="chat-send-btn" onClick={() => submit()} disabled={streaming || locked || !text.trim()}
            className="w-9 h-9 grid place-items-center bg-[var(--cyan)] text-[var(--bg)] disabled:opacity-30 hover:brightness-110 transition-[filter,opacity]">
            {streaming ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};
